import { LoaderCircle, Save, X } from "lucide-react";
import { t } from "../../i18n";

export type EditableOrganization = {
  id: string;
  name: string;
  slug: string;
  locale: string;
  timezone: string;
  state?: string;
  currency?: string;
  retentionDays?: number;
  role?: string;
};

export function OrganizationEditor({
  value,
  saving,
  siteFields = false,
  title = "Edit organization",
  saveLabel = "Save organization",
  onChange,
  onSave,
  onCancel,
}: {
  value: EditableOrganization;
  saving: boolean;
  siteFields?: boolean;
  title?: string;
  saveLabel?: string;
  onChange: (value: EditableOrganization) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <section className="panel p-5">
      <div className="flex items-center justify-between">
        <h2 className="font-bold">{t(title)}</h2>
        <button className="button" onClick={onCancel}>
          <X size={15} />
        </button>
      </div>
      <div className="mt-4 grid gap-3 md:grid-cols-3">
        <input
          className="field"
          value={value.name}
          placeholder={t("Name")}
          onChange={(event) => onChange({ ...value, name: event.target.value })}
        />
        <input
          className="field"
          value={value.slug}
          placeholder={t("Slug")}
          onChange={(event) =>
            onChange({ ...value, slug: event.target.value.toLowerCase() })
          }
        />
        <input
          className="field"
          value={value.locale}
          placeholder={t("Locale")}
          onChange={(event) =>
            onChange({ ...value, locale: event.target.value })
          }
        />
        <input
          className="field"
          value={value.timezone}
          placeholder={t("Timezone")}
          onChange={(event) =>
            onChange({ ...value, timezone: event.target.value })
          }
        />
        {siteFields && (
          <>
            <select
              className="field"
              value={value.state}
              onChange={(event) =>
                onChange({ ...value, state: event.target.value })
              }
            >
              {[
                "trial",
                "active",
                "past_due",
                "restricted",
                "suspended",
                "cancelled",
              ].map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
            <input
              className="field"
              value={value.currency}
              maxLength={3}
              placeholder={t("Currency")}
              onChange={(event) =>
                onChange({
                  ...value,
                  currency: event.target.value.toUpperCase(),
                })
              }
            />
            <label className="text-sm">
              <span className="label">{t("Retention days")}</span>
              <input
                className="field"
                type="number"
                min={1}
                max={3650}
                value={value.retentionDays}
                onChange={(event) =>
                  onChange({
                    ...value,
                    retentionDays: Number(event.target.value),
                  })
                }
              />
            </label>
          </>
        )}
      </div>
      <div className="mt-4 flex justify-end">
        <button
          className="button-primary"
          disabled={saving || !value.name || !value.slug}
          onClick={onSave}
        >
          {saving ? (
            <LoaderCircle className="animate-spin" size={15} />
          ) : (
            <Save size={15} />
          )}
          {t(saveLabel)}
        </button>
      </div>
    </section>
  );
}
